import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { Block } from "@mui/icons-material";
import { voidInvoice } from "./api";
import type { InvoiceByPeriodItem } from "./types";
import DialogHeader from "./DialogHeader";

interface VoidInvoiceDialogProps {
  target: InvoiceByPeriodItem | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const VoidInvoiceDialog: React.FC<VoidInvoiceDialogProps> = ({
  target,
  onClose,
  onSuccess,
  onError,
}) => {
  const [voiding, setVoiding] = useState(false);

  const handleVoid = async () => {
    if (!target) return;
    try {
      setVoiding(true);
      await voidInvoice(target.id);
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error voiding invoice:", err);
      onError("Failed to void invoice. Please try again.");
    } finally {
      setVoiding(false);
    }
  };

  return (
    <Dialog
      open={target != null}
      onClose={() => !voiding && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogHeader
        title="Void Invoice"
        icon={<Block color="warning" />}
        onClose={onClose}
        closeDisabled={voiding}
      />
      <DialogContent dividers>
        <Typography variant="body1">
          Are you sure you want to void the invoice for{" "}
          <strong>{target?.handler_name}</strong> (£
          {target?.amount.toFixed(2)})?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The member will be emailed to let them know no payment is required.
          The invoice will remain visible as voided.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={voiding}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          startIcon={
            voiding ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <Block />
            )
          }
          onClick={handleVoid}
          disabled={voiding}
        >
          {voiding ? "Voiding…" : "Void Invoice"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoidInvoiceDialog;
